// File: src/products/equityManager/pages/SummaryByRoundPage.js
// Summary by Round page component - shows cap table evolution through funding rounds

import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, TrendingUp, Users, DollarSign, FileText, Download, List, Calendar } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { 
    calculateCompleteRoundSummary, 
    prepareDilutionChartData, 
    generateShareholderColors,
    calculateOverviewStats 
} from '../utils/capTableCalculations';
import { useActivityLog } from '../../../hooks/useActivityLog';
import formatDate from '../utils/dateFormatter';
import formatNumberWithUnits from '../utils/numberFormatter';
import formatCurrencyWithUnits from '../utils/currencyFormatter';

const SummaryByRoundPage = ({ selectedCompany, companyData, issuances, addMessage }) => {
    // Log page access
    useActivityLog('Summary by Round', selectedCompany?.id);
    
    const [roundData, setRoundData] = useState([]);
    const [loading, setLoading] = useState(true);

    const calculateRoundSummary = useCallback(() => {
        // Use issuances prop directly instead of companyData.shareIssuances
        const shareIssuances = issuances || companyData?.shareIssuances || [];
        
        if (!shareIssuances || shareIssuances.length === 0) {
            setRoundData([]);
            setLoading(false);
            return;
        }

        try {
            const roundSummaries = calculateCompleteRoundSummary(shareIssuances);
            setRoundData(roundSummaries);
        } catch (error) {
            console.error('Error calculating round summary:', error);
            setRoundData([]);
        } finally {
            setLoading(false);
        }
    }, [issuances, companyData]);

    // Calculate chart data and colors using utility functions
    const dilutionChartData = prepareDilutionChartData(roundData);
    const shareholderColors = generateShareholderColors(roundData);
    const overviewStats = calculateOverviewStats(roundData);

    useEffect(() => {
        if (selectedCompany) {
            calculateRoundSummary();
        }
    }, [selectedCompany, calculateRoundSummary]);

    const formatCurrency = (amount) => {
        // Use K/M formatting for large currency amounts
        return formatCurrencyWithUnits(amount, selectedCompany?.currency || 'USD');
    };

    const formatNumber = (num) => {
        if (num === null || num === undefined || isNaN(num)) return '0';
        // Use K/M formatting for large numbers
        return formatNumberWithUnits(num);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!selectedCompany) {
        return (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
                <BarChart3 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Company Selected</h3>
                <p className="text-gray-600">
                    Please select a company to view the summary by round.
                </p>
            </div>
        );
    }

    if (roundData.length === 0) {
        return (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
                <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Share Issuances Found</h3>
                <p className="text-gray-600">
                    Add some share issuances to see the cap table summary by round.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-8 p-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <BarChart3 className="h-8 w-8 text-blue-600" />
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Summary by Round</h1>
                            <p className="text-gray-600">{selectedCompany.name}</p>
                        </div>
                    </div>
                    <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">
                        <Download className="h-4 w-4" />
                        <span>Export PDF</span>
                    </button>
                </div>

                {/* Overview Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex items-center space-x-2">
                            <TrendingUp className="h-5 w-5 text-blue-600" />
                            <span className="text-sm font-medium text-blue-900">Total Rounds</span>
                        </div>
                        <p className="text-2xl font-bold text-blue-900 mt-1">{overviewStats.totalRounds}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                        <div className="flex items-center space-x-2">
                            <Users className="h-5 w-5 text-green-600" />
                            <span className="text-sm font-medium text-green-900">Total Shareholders</span>
                        </div>
                        <p className="text-2xl font-bold text-green-900 mt-1">
                            {overviewStats.totalShareholders}
                        </p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                        <div className="flex items-center space-x-2">
                            <FileText className="h-5 w-5 text-purple-600" />
                            <span className="text-sm font-medium text-purple-900">Shares Outstanding</span>
                        </div>
                        <p className="text-2xl font-bold text-purple-900 mt-1">
                            {formatNumber(overviewStats.totalSharesOutstanding)}
                        </p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4">
                        <div className="flex items-center space-x-2">
                            <DollarSign className="h-5 w-5 text-orange-600" />
                            <span className="text-sm font-medium text-orange-900">Total Capital Raised</span>
                        </div>
                        <p className="text-2xl font-bold text-orange-900 mt-1">
                            {formatCurrency(overviewStats.totalCapitalRaised)}
                        </p>
                    </div>
                </div>
            </div>

            {/* Dilution Chart */}
            {roundData.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden mb-8">
                    <div className="bg-gray-50 px-6 py-4 border-b">
                        <div className="flex items-center space-x-2">
                            <TrendingUp className="h-5 w-5 text-gray-600" />
                            <h2 className="text-lg font-semibold text-gray-900">Dilution Analysis</h2>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                            Ownership percentage by shareholder across funding rounds
                        </p>
                    </div>
                    <div className="p-6">
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart
                                data={dilutionChartData}
                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                <XAxis 
                                    dataKey="round" 
                                    tick={{ fontSize: 12 }}
                                    tickLine={{ stroke: '#9CA3AF' }}
                                />
                                <YAxis 
                                    tick={{ fontSize: 12 }}
                                    tickLine={{ stroke: '#9CA3AF' }}
                                    domain={[0, 100]}
                                    tickFormatter={(value) => `${value}%`}
                                />
                                <Tooltip 
                                    formatter={(value, name) => [`${value}%`, name]}
                                    labelFormatter={(label) => `${label}`}
                                    contentStyle={{
                                        backgroundColor: '#FFFFFF',
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '6px',
                                        fontSize: '12px'
                                    }}
                                />
                                <Legend 
                                    wrapperStyle={{ fontSize: '12px' }}
                                    iconType="rect"
                                />
                                {Object.keys(shareholderColors).map(shareholder => (
                                    <Bar
                                        key={shareholder}
                                        dataKey={shareholder}
                                        stackId="ownership"
                                        fill={shareholderColors[shareholder]}
                                        stroke="#FFFFFF"
                                        strokeWidth={1}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                        
                        {/* Chart Legend/Key */}
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Chart Insights:</h4>
                            <ul className="text-xs text-gray-600 space-y-1">
                                <li>• Each bar represents 100% of company ownership at the end of that round</li>
                                <li>• Colors show individual shareholder ownership percentages</li>
                                <li>• Observe how shareholdings get diluted with each new funding round</li>
                                <li>• New shareholders appear when they first receive equity</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* Rounds Summary Table */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b">
                    <div className="flex items-center space-x-2">
                        <List className="h-5 w-5 text-gray-600" />
                        <h2 className="text-lg font-semibold text-gray-900">Rounds Overview</h2>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Round
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Date
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    New Shares
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Capital Raised
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Share Price Range
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Total Shares Outstanding
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Shareholders
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {roundData.map((round, index) => {
                                const minPrice = Math.min(...round.issuances.map(i => parseFloat(i.price_per_share) || 0));
                                const maxPrice = Math.max(...round.issuances.map(i => parseFloat(i.price_per_share) || 0));
                                
                                return (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {round.roundName}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                Round {round.roundNumber}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDate(round.earliestDate)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                            {formatNumber(round.roundTotalShares)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                            {formatCurrency(round.roundTotalValue)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                            {minPrice === maxPrice 
                                                ? formatCurrency(minPrice)
                                                : `${formatCurrency(minPrice)} - ${formatCurrency(maxPrice)}`
                                            }
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                            {formatNumber(round.totalSharesOutstanding)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                            {round.capTable.length}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Round by Round Detailed Summary */}
            {roundData.map((round, roundIndex) => (
                <div key={roundIndex} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                    {/* Round Header */}
                    <div className="bg-gray-50 px-6 py-4 border-b">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">
                                    {round.roundName}
                                </h2>
                                <div className="flex items-center space-x-4 mt-1">
                                    <div className="flex items-center space-x-1 text-sm text-gray-600">
                                        <Calendar className="h-4 w-4" />
                                        <span>{formatDate(round.earliestDate)}</span>
                                    </div>
                                    <span className="text-sm text-gray-600">
                                        {round.issuances.length} issuance{round.issuances.length !== 1 ? 's' : ''}
                                    </span>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-gray-600">Round Total</p>
                                <p className="text-lg font-semibold text-gray-900">
                                    {formatCurrency(round.roundTotalValue)}
                                </p>
                                <p className="text-sm text-gray-500">
                                    {formatNumber(round.roundTotalShares)} shares
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Round Issuances */}
                    <div className="p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">New Issuances</h3>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Shareholder
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Share Class
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Shares
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Price/Share
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Issued Capital
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Issue Date
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {round.issuances.map((issuance, idx) => (
                                        <tr key={idx}>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                {issuance.shareholder_name || `Shareholder ${issuance.shareholder_id}`}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600">
                                                {issuance.share_class_name || issuance.share_class?.name || 'Common'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                {formatNumber(issuance.shares)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                {formatCurrency(issuance.price_per_share)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                                                {formatCurrency(issuance.shares * issuance.price_per_share)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600">
                                                {formatDate(issuance.issue_date)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-gray-50">
                                    <tr>
                                        <td className="px-4 py-3 text-sm font-bold text-gray-900" colSpan="2">
                                            Round {round.roundNumber} Total
                                        </td>
                                        <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                            {formatNumber(round.roundTotalShares)}
                                        </td>
                                        <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                            {round.averagePrice > 0 ? formatCurrency(round.averagePrice) : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                            {formatCurrency(round.roundTotalValue)}
                                        </td>
                                        <td className="px-4 py-3"></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>

                    {/* Cap Table After Round */}
                    <div className="border-t bg-gray-50 p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Cap Table After {round.roundName}
                        </h3>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Shareholder
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Share Class
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Total Shares
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            % Ownership
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actual Cash Invested
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Value at Latest Price
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {round.capTable.map((holder, idx) => (
                                        <tr key={idx}>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                {holder.shareholderName}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600">
                                                {holder.shareClass?.name || holder.shareClass || 'Common'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                {formatNumber(holder.totalShares)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    holder.percentage >= 50 ? 'bg-red-100 text-red-800' :
                                                    holder.percentage >= 25 ? 'bg-yellow-100 text-yellow-800' :
                                                    holder.percentage >= 10 ? 'bg-blue-100 text-blue-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {holder.percentage.toFixed(1)}%
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                {formatCurrency(holder.totalValue)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                                                {formatCurrency(holder.valueAtHighestPrice)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-gray-100">
                                    <tr>
                                        <td className="px-4 py-3 text-sm font-bold text-gray-900" colSpan="2">
                                            Total Outstanding
                                        </td>
                                        <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                            {formatNumber(round.totalSharesOutstanding)}
                                        </td>
                                        <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                100.0%
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                            {formatCurrency(round.capTable.reduce((sum, holder) => sum + holder.totalValue, 0))}
                                        </td>
                                        <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                            {formatCurrency(round.totalSharesOutstanding * round.highestPrice)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default SummaryByRoundPage;